import { useEffect, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, useCurrentCompany, useMe } from '../../lib'
import { ErrorBox, Loading } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import { bankSetupStarted, endBankSetup } from './bankSetup'
import type { Recipient } from './types'

/** Back from the hosted bank form. We ask the server, which reads the provider; the redirect proves nothing.
 *  Only an admin of the current company who started a bank setup in this tab sees the result; anyone else
 *  (no company, another company's id, a member, no setup, manual payouts) goes to /app. */
export function BankReturnPage() {
  const { me, isLoading } = useMe()
  const current = useCurrentCompany()
  const [params] = useSearchParams()
  const companyId = params.get('company_id')
  // read once: the marker is cleared when the bank is ready, and the page must keep showing that
  const [started] = useState(() => !!companyId && bankSetupStarted(companyId))
  const allowed = started && !!current && current.role === 'admin' && companyId === current.id
  const status = useQuery({
    queryKey: ['redeem', 'recipient', companyId],
    queryFn: () => api<Recipient>('GET', `/api/companies/${companyId}/payout-recipient`),
    enabled: allowed,
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 3000 : false),
  })
  const r = status.data
  useEffect(() => {
    if (r?.status === 'ready') endBankSetup()
  }, [r?.status])

  if (isLoading || !me) return <div className="page"><Loading what="Checking your bank account" /></div>
  if (!allowed || (r && !['ready', 'pending'].includes(r.status))) return <Navigate to="/" replace />
  if (status.error) return <ErrorBox error={status.error} />
  return (
    <div className="page stack">
      <PageBand title="Bank account" />
      {status.isLoading && <Loading what="Checking your bank account" />}
      {r?.status === 'ready' && (
        <div className="panel stack" data-testid="redeem-bank-ready">
          <p>Bank account added: {r.bank_name} ending {r.bank_last4}.</p>
          <p>We pay out to it each month.</p>
          <Link className="button primary" to="/redeem">Back to Redeem</Link>
        </div>
      )}
      {r?.status === 'pending' && (
        <div className="panel" data-testid="redeem-bank-pending">
          <p>We have not confirmed your bank account yet. This page checks again in a few seconds.</p>
          <Link to="/redeem">Back to Redeem</Link>
        </div>
      )}
    </div>
  )
}
